import { useMemo, useState } from 'react';

import { CheckCircle2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

import { GUIDE_ITEMS_CONFIG } from '../constants/guide-items';
import { useDismissGuide } from '../hooks/use-dismiss-guide';
import { GuideStatusItem } from '../types/guide';
import { GuideDismissDialog } from './guide-dismiss-dialog';
import { GuideItem } from './guide-item';

interface GuidePopoverProps {
  items: GuideStatusItem[];
  onClosePopover: () => void;
}

export function GuidePopover({ items, onClosePopover }: GuidePopoverProps) {
  const { t } = useTranslation('authenticated.onboarding-checklist');
  const [showDismissDialog, setShowDismissDialog] = useState(false);
  const dismissGuide = useDismissGuide();

  const completionMap = useMemo(() => {
    const map = new Map<string, boolean>();
    items.forEach(item => map.set(item.key, item.completed));
    return map;
  }, [items]);

  const completedCount = items.filter(item => item.completed).length;
  const totalCount = items.length;
  const allCompleted = completedCount === totalCount;

  const handleClose = () => {
    dismissGuide.mutate(undefined, {
      onSuccess: () => onClosePopover(),
    });
  };

  if (allCompleted) {
    return (
      <div className="flex flex-col items-center px-6 py-8 text-center">
        <CheckCircle2 className="mb-3 size-10 text-green-600" />
        <h3 className="text-base font-semibold">{t('popover.successTitle')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t('popover.successDescription')}</p>
        <Button className="mt-4" onClick={handleClose} disabled={dismissGuide.isPending}>
          {t('popover.closeButton')}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col">
        <div className="flex items-start justify-between px-4 pb-2 pt-4">
          <div>
            <h3 className="text-base font-semibold">{t('popover.title')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('popover.progress', { completed: completedCount, total: totalCount })}
            </p>
          </div>
          <button
            onClick={onClosePopover}
            className="rounded-sm p-1 opacity-70 transition-opacity hover:opacity-100"
          >
            <X className="size-4" />
          </button>
        </div>

        <Separator />

        <div className="max-h-[400px] overflow-y-auto">
          <div className="flex flex-col gap-0.5 px-2 py-2">
            {GUIDE_ITEMS_CONFIG.filter(
              config =>
                completionMap.has(config.key) ||
                config.subItems?.some(sub => completionMap.has(sub.key))
            ).map((config, index) => (
              <GuideItem
                key={config.key}
                config={config}
                index={index}
                completionMap={completionMap}
                onClosePopover={onClosePopover}
              />
            ))}
          </div>
        </div>

        <Separator />

        <div className="px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            onClick={() => setShowDismissDialog(true)}
          >
            {t('dismiss.button')}
          </Button>
        </div>
      </div>

      <GuideDismissDialog open={showDismissDialog} onOpenChange={setShowDismissDialog} />
    </>
  );
}
